// VENDORED from extensions/packages/simone/symbols/transport/ws.js — keep in
// sync with the source (same reason as ./sendPrompt.js: a bare simone import
// breaks CI smbls build). Used by ../simoneAiTransport.js for Local mode:
// createDirectWsPort + handleBridgePayload/handleAgentEvent give the
// workspace AssistantDrawer a real simone-bridge round-trip.

// WebSocket transport — used by chrome-connect (panel ↔ service worker port)
// and by any host that wants raw WS to a local Claude Code bridge.
//
// Host must provide a `port` with .postMessage and .onMessage.addListener
// (mirrors chrome.runtime.Port API). For non-extension hosts, see
// `createDirectWsPort(url)` at the bottom of this file.

const pendingTools = new Map()
const newId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const upsertMessage = (root, id, patch) => {
  const list = (root.chatMessages || []).slice()
  const idx = list.findIndex(m => m.id === id)
  if (idx === -1) list.push({ id, ...patch })
  else list[idx] = { ...list[idx], ...patch }
  root.update({ chatMessages: list })
}

const pushSystem = (root, text) => {
  const list = (root.chatMessages || []).concat({
    id: newId(), role: 'system', text, ts: Date.now()
  })
  root.update({ chatMessages: list })
}

// Exported for non-extension hosts (workspace drawer's Local mode) that embed
// their own port wiring instead of wsTransport() — they reuse the SAME payload
// handling so bridge behaviour can't drift between surfaces.
export const handleBridgePayload = (root, payload, sendToPage, sendToBridge) => {
  if (!payload || !payload.type) return

  if (payload.type === 'bridge_ready') {
    // Bridge surfaces the resolved default target. The picker is opt-in
    // (chat works against the launch default), so targetSelected is mostly
    // informational here.
    root.update({
      chatTyping: false,
      agent: payload.agent || null,
      target: payload.target || null,
      targetSelected: !!payload.targetSelected,
      targetDraftMode: (payload.target && payload.target.mode) || null,
      targetDraftCwd: (payload.target && payload.target.cwd) || null,
      cliAuth: payload.cliAuth || null
    })
    // Eagerly request the target catalog so the picker has data the moment
    // the user opens it. WS handshake just completed; sendToBridge is safe.
    if (typeof sendToBridge === 'function') {
      try { sendToBridge({ type: 'list_targets' }) } catch (_) {}
    }
    // Auto-restore saved target across reconnects: if the current origin
    // has a localStorage pairing AND the bridge hasn't already bound a
    // target this session, replay select_target so the user keeps their
    // chosen cwd through parcel HMR / claude SIGTERM / network blips.
    if (typeof sendToBridge === 'function' && !payload.targetSelected) {
      try {
        const origin = (root.currentOrigin && typeof root.currentOrigin === 'string')
          ? root.currentOrigin
          : (typeof window !== 'undefined' && window.location && window.location.origin) || null
        if (origin && typeof window !== 'undefined' && window.localStorage) {
          const pairings = JSON.parse(window.localStorage.getItem('symbols_recent_pairings') || '{}')
          const saved = pairings && pairings[origin] && pairings[origin].target
          if (saved && (saved.cwd || saved.mode)) sendToBridge({ type: 'select_target', target: saved })
        }
      } catch (_) {}
    }
    return
  }
  if (payload.type === 'session_ready') {
    root.update({ chatTyping: false })
    return
  }
  if (payload.type === 'target_options') {
    root.update({ targetOptions: payload })
    return
  }
  if (payload.type === 'target_active') {
    const origin = (root.currentOrigin && typeof root.currentOrigin === 'string')
      ? root.currentOrigin
      : (typeof window !== 'undefined' && window.location && window.location.origin) || null
    const target = payload.target || null
    const nextMode = target && target.mode === 'cloud' ? 'cloud' : (target && target.cwd ? 'local' : 'general')
    const baseUpdate = {
      target,
      targetSelected: true,
      targetMode: nextMode,
      targetDraftCustom: '',
      targetPickerOpen: false,
      targetPickerView: 'home'
    }
    if (origin && target) {
      try {
        const prev = JSON.parse(window.localStorage.getItem('symbols_recent_pairings') || '{}')
        const label = target.mode === 'cloud'
          ? (target.owner && target.key ? `${target.owner}/${target.key}` : 'cloud project')
          : (target.cwd ? target.cwd.split('/').filter(Boolean).pop() : '(unnamed)')
        prev[origin] = { target, label, ts: Date.now() }
        window.localStorage.setItem('symbols_recent_pairings', JSON.stringify(prev))
        root.update({ ...baseUpdate, recentPairings: prev })
      } catch (_) {
        root.update(baseUpdate)
      }
    } else {
      root.update(baseUpdate)
    }
    return
  }
  if (payload.type === 'workspaces_response') {
    root.update({
      cloudWorkspaces: Array.isArray(payload.workspaces) ? payload.workspaces : [],
      cloudWorkspacesLoading: false,
      cloudWorkspacesError: payload.error || null,
      // If bridge resolved a user email (from /auth/me), record it.
      cliAuth: payload.me
        ? { ...(root.cliAuth || {}), email: payload.me.email || (root.cliAuth && root.cliAuth.email), userId: payload.me.id || (root.cliAuth && root.cliAuth.userId), apiBaseUrl: payload.apiBaseUrl || (root.cliAuth && root.cliAuth.apiBaseUrl) }
        : (root.cliAuth || null)
    })
    return
  }
  if (payload.type === 'target_locked') {
    pushSystem(root, payload.reason || 'Target already locked. /reset to swap.')
    return
  }
  if (payload.type === 'session_reset') {
    // Bridge cleared the child — user may pick a fresh target.
    root.update({
      chatMessages: [],
      chatTyping: false,
      targetSelected: false,
      target: payload.target || root.target
    })
    return
  }
  if (payload.type === 'status') {
    root.update({ chatTyping: payload.status === 'requesting' })
    return
  }
  if (payload.type === 'assistant_start') {
    // Lazy bubble — don't materialize an empty "Claude" row yet. Many turns
    // open with a thinking block (ignored) or a tool_use block (rendered as
    // its own card), then never emit text. Creating the bubble up-front
    // leaves a visually empty header. We defer creation until the first
    // assistant_delta lands.
    const id = payload.messageId || 'assistant-current'
    root.update({ chatTyping: true, currentAssistantId: id })
    return
  }
  if (payload.type === 'assistant_delta') {
    const id = root.currentAssistantId || 'assistant-current'
    const list = root.chatMessages || []
    const existing = list.find(m => m.id === id)
    const blocks = { ...((existing && existing.blocks) || {}) }
    const k = payload.blockIndex || 0
    blocks[k] = (blocks[k] || '') + (payload.text || '')
    // Named `bi` (block index), not a single letter: frank's pipeline free-var
    // scanner does not descend into a nested arrow's parameter list, so a
    // one-letter param collides with a MINIFIED top-level name in the esbuild
    // bundle and frank lifts that stranger into globalScope — `j` pulled
    // symbolize-markdown's minified renderer in and blocked the publish gate
    // (WORKSPACE-SHELL-FRANK-GATE-BLOCKS-REPUBLISH-1; frank defect filed).
    const text = Object.keys(blocks).sort((a, b) => Number(a) - Number(b)).map(bi => blocks[bi]).join('')
    // First non-empty delta lazily creates the bubble; later deltas update it.
    upsertMessage(root, id, { role: 'assistant', text, blocks, streaming: true, ts: Date.now() })
    return
  }
  if (payload.type === 'thinking_delta') return
  if (payload.type === 'tool_use_start' || payload.type === 'tool_use') {
    const messageId = `tool-${payload.toolUseId || newId()}`
    upsertMessage(root, messageId, {
      role: 'tool',
      toolName: payload.tool,
      toolUseId: payload.toolUseId,
      argsText: payload.input ? JSON.stringify(payload.input, null, 2) : '',
      status: 'pending',
      ts: Date.now()
    })
    const input = payload.input || {}
    if (sendToPage && payload.tool === 'sdk_execute') {
      pendingTools.set(payload.toolUseId, { messageId, toolUseId: payload.toolUseId })
      sendToPage({ type: 'sdk_execute', id: payload.toolUseId, ...input })
    } else if (sendToPage && payload.tool === 'pick_element') {
      pendingTools.set(payload.toolUseId, { messageId, toolUseId: payload.toolUseId })
      sendToPage({ type: 'start_pick' })
    } else if (sendToPage && payload.tool === 'inspect_$0') {
      pendingTools.set(payload.toolUseId, { messageId, toolUseId: payload.toolUseId })
      sendToPage({ type: 'inspect_selected', id: payload.toolUseId })
    }
    return
  }
  if (payload.type === 'tool_result') {
    const messageId = `tool-${payload.toolUseId}`
    upsertMessage(root, messageId, {
      status: payload.isError ? 'error' : 'done',
      resultText: typeof payload.content === 'string'
        ? payload.content
        : JSON.stringify(payload.content, null, 2)
    })
    return
  }
  if (payload.type === 'assistant_stop' || payload.type === 'turn_complete') {
    const id = root.currentAssistantId
    if (id) {
      // Mark the existing message done — never create a new bubble here.
      const list = (root.chatMessages || []).map(m =>
        m.id === id ? { ...m, streaming: false } : m
      )
      root.update({ chatMessages: list })
    }
    root.update({ chatTyping: false, currentAssistantId: null })
    return
  }
  if (payload.type === 'claude_error') {
    pushSystem(root, 'Claude bridge error: ' + (payload.error || 'unknown'))
    root.update({ chatTyping: false })
    return
  }
  if (payload.type === 'claude_exited') {
    pushSystem(root, `Claude subprocess exited (code=${payload.code}). Send a message to restart.`)
    root.update({ chatTyping: false })
    return
  }
  if (payload.type === 'agent_stuck') {
    pushSystem(root, payload.reason || 'Agent looks stuck — no output for a while. Try /reset.')
    root.update({ chatTyping: false })
    return
  }
}

export const handleAgentEvent = (root, ev, sendToBridge) => {
  if (!ev || !ev.type) return
  if (ev.type === 'picked_element') {
    const list = (root.pickedElements || []).slice()
    const sig = (ev.info && ev.info.tagPath || []).join('>')
    const exists = list.some(p => (p.tagPath || []).join('>') === sig)
    if (!exists && ev.info) list.push(ev.info)
    root.update({ pickedElements: list })
    return
  }
  if (ev.type === 'pick_cancelled') { root.update({ picking: false }); return }
  if (ev.type === 'pick_started')   { root.update({ picking: true });  return }
  if (ev.type === 'inspect_selected_result') {
    if (ev.info && !ev.info.error) {
      root.update({ pickedElements: (root.pickedElements || []).concat(ev.info) })
    } else {
      pushSystem(root, ev.info && ev.info.error || 'No $0 selected.')
    }
    return
  }
  if (ev.type === 'sdk_execute_result') {
    const pending = pendingTools.get(ev.id)
    if (!pending) return
    pendingTools.delete(ev.id)
    upsertMessage(root, pending.messageId, {
      status: ev.ok ? 'done' : 'error',
      resultText: ev.ok ? JSON.stringify(ev.result, null, 2) : ev.error
    })
    if (sendToBridge) {
      sendToBridge({
        type: 'tool_result',
        toolUseId: pending.toolUseId,
        result: ev.ok ? ev.result : { error: ev.error },
        isError: !ev.ok
      })
    }
  }
}

// Install onto a smbls context — adds `initBridge`, `configureBridge`, and
// `sendOverBridge` functions. The host provides `getPort()`, `sendToPage` and
// (optionally) `subscribeRuntime()` for the chrome.runtime.onMessage fan-in.
export const wsTransport = ({ getPort, sendToPage = null, subscribeRuntime = null, surface = null } = {}) => {
  // Closed-over by both initBridge (writes) and sendOverBridge (reads) so the
  // component layer can `el.call('sendOverBridge', payload)` without poking
  // any globals.
  let _send = null

  return {
    initBridge: function () {
      const root = this.state.root
      const port = getPort()
      if (!port) return

      const sendToBridge = (payload) => {
        try { port.postMessage({ type: 'send', payload }) } catch (_) {}
      }
      _send = sendToBridge

      // Declare the host surface to the bridge so it can scope MCP servers
      // (e.g. skip the inspected-tab MCP for standalone tabs that don't
      // have an extension behind them to drive the inspected page).
      if (surface) {
        try { sendToBridge({ type: 'hello', surface }) } catch (_) {}
      }

      const dispatch = (msg) => {
        if (!msg) return
        if (msg.type === 'bridge_status') {
          root.update({ bridgeStatus: msg.status, bridgeUrl: msg.url || root.bridgeUrl })
          return
        }
        if (msg.type === 'bridge_message') {
          handleBridgePayload(root, msg.payload, sendToPage, sendToBridge)
          return
        }
        // Inspected-tab tool calls coming back from claude (via the bundled
        // inspected-mcp server). Route to the panel-side executor.
        if (msg.type === 'inspected_tool_call') {
          if (typeof window.__simoneHandleInspectedTool === 'function') {
            window.__simoneHandleInspectedTool(msg.payload)
          }
          return
        }
        handleAgentEvent(root, msg, sendToBridge)
      }

      window.__simoneDispatch = dispatch
      window.__simoneSendOverBridge = sendToBridge
      port.onMessage.addListener(dispatch)

      if (subscribeRuntime) {
        try { subscribeRuntime(dispatch) } catch (_) {}
      }
    },

    // Send an arbitrary payload to the bridge. Components call this via
    // `el.call('sendOverBridge', payload)` for select_target, list_targets,
    // reset, cancel, etc. No-op until initBridge has wired the port.
    sendOverBridge: function (payload) {
      if (typeof _send === 'function') _send(payload)
    },

    configureBridge: function (url) {
      const root = this.state.root
      const next = (url || '').trim() || 'ws://localhost:8765'
      const perTab = root.perTabSessions !== false
      root.update({ bridgeUrl: next, showSettings: false })
      const port = getPort()
      if (port) try { port.postMessage({ type: 'configure', url: next, perTab }) } catch (_) {}
    }
  }
}

// Stable per-origin bridge session id. Persisted so reconnects (HMR reload,
// claude SIGTERM, network blip) rebind to the SAME bridge session instead of
// minting a fresh one each time — the bridge keeps the agent child alive
// through a short grace window keyed on this id. Without it every reconnect
// spawned a new session + staged a new MCP config (the connection-log storm).
// sessionStorage (NOT localStorage) is deliberate: it's scoped per browsing
// context, so the id is stable across reloads/HMR within a tab but unique
// between tabs — two same-origin tabs never collide onto one bridge session
// (which would have them fight over a single agent child).
const SHELL_STORAGE_KEY = 'symbols_bridge_session'
const bridgeSessionId = () => {
  const mint = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return mint()
    let id = window.sessionStorage.getItem(SHELL_STORAGE_KEY)
    if (!id) { id = mint(); window.sessionStorage.setItem(SHELL_STORAGE_KEY, id) }
    return id
  } catch (_) { return mint() }
}

// Append ?sid=<id> to the bridge URL so the server (which can't read custom
// headers off a browser WebSocket) can key the session off the query string.
const withSession = (url, sid) => {
  try {
    const u = new URL(url, (typeof window !== 'undefined' && window.location && window.location.href) || undefined)
    u.searchParams.set('sid', sid)
    return u.toString()
  } catch (_) {
    return url + (url.includes('?') ? '&' : '?') + 'sid=' + encodeURIComponent(sid)
  }
}

// Helper: create a direct WS port (no chrome.runtime layer) that mimics the
// chrome.runtime.Port shape. Useful for non-extension hosts that want to
// talk to the bridge directly.
//
// Reconnect policy: exponential backoff with jitter (1s → 30s), reset on a
// successful open. Reconnects are PAUSED while the tab is hidden and fire
// immediately when it becomes visible again — a backgrounded tab whose bridge
// flaps no longer hammers the server with a connection every 2.5s.
export const createDirectWsPort = (url) => {
  const listeners = new Set()
  const sid = bridgeSessionId()
  const target = withSession(url, sid)

  const BASE_DELAY = 1000
  const MAX_DELAY = 30000

  let ws = null
  let reconnectTimer = null
  let attempt = 0
  let stopped = false

  const emit = (msg) => listeners.forEach(l => l(msg))

  const clearTimer = () => {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
  }

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer) return
    // Don't burn connections against a hidden tab — wait for visibility.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    const backoff = Math.min(MAX_DELAY, BASE_DELAY * 2 ** attempt)
    const delay = backoff / 2 + Math.random() * (backoff / 2) // 50–100% jitter
    attempt += 1
    reconnectTimer = setTimeout(() => { reconnectTimer = null; connect() }, delay)
  }

  const connect = () => {
    if (stopped) return
    clearTimer()
    if (ws && (ws.readyState === 0 || ws.readyState === 1)) return // already (re)connecting
    ws = new WebSocket(target)
    ws.onopen = () => {
      attempt = 0 // reset backoff on a clean open
      emit({ type: 'bridge_status', status: 'open', url })
    }
    ws.onclose = () => {
      emit({ type: 'bridge_status', status: 'closed', url })
      scheduleReconnect()
    }
    ws.onerror = () => emit({ type: 'bridge_status', status: 'error', url })
    ws.onmessage = (ev) => {
      let parsed
      try { parsed = JSON.parse(ev.data) } catch (_) { parsed = { type: 'raw', text: String(ev.data) } }
      emit({ type: 'bridge_message', payload: parsed })
    }
  }

  // Reconnect the moment the tab is foregrounded again (with a fresh backoff
  // budget), instead of waiting out a timer scheduled before it was hidden.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      if (stopped || document.visibilityState !== 'visible') return
      if (ws && ws.readyState === 1) return // still connected, nothing to do
      attempt = 0
      clearTimer()
      connect()
    })
  }

  connect()
  return {
    postMessage: (msg) => {
      if (!ws || ws.readyState !== 1) return
      if (msg.type === 'send' && msg.payload) {
        ws.send(JSON.stringify(msg.payload))
      } else if (msg.type === 'configure') {
        // direct port can't be reconfigured without recreating
      }
    },
    onMessage: { addListener: (fn) => listeners.add(fn) },
    // Intentional teardown — stops the reconnect loop so a recreated port
    // doesn't leave an orphan socket retrying in the background.
    close: () => {
      stopped = true
      clearTimer()
      if (ws) { try { ws.close() } catch (_) {} }
    }
  }
}
